/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Config
import physiomKineApi from "@/configs/physiom-kine-api.config";

// Helpers
import { makeUrl } from "@/helpers/api.helper";

// Types
import { Meeting, MeetingStatus } from "@/types/meeting";

/* -------------------------------------------------------------------------- */
/*                               Service class                                */
/* -------------------------------------------------------------------------- */
type GetMeetingsArgs = {
  search?: string;
  status?: string;
  meetingDate?: string;
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  reportDone?: boolean;
};

type GetBeneficiaryMeetingsArgs = {
  beneficiaryId: string;
  search?: string;
  status?: string;
  meetingDate?: string;
  startDate?: string;
  endDate?: string;
  reportDone?: boolean;
};

type GetMeetingsApiResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Meeting[];
};

type CreateMeetingArgs = {
  beneficiaryId: string;
  meetingDate: string;
  startAt: string;
};

type UpdateMeetingArgs = {
  id: string;
  data: Partial<CreateMeetingArgs> & {
    status?: MeetingStatus;
    observationEvolution?: string;
    observationExercises?: string;
    observationObjectifs?: string;
    connectionTrouble?: boolean;
    isReportDone?: boolean;
  };
};

class MeetingService {
  async get(args: GetMeetingsArgs) {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({
        path: "/tele-consultation/kine/",
        searchParams: {
          search: args.search,
          page: args.page,
          pageSize: args.pageSize,
          status: args.status,
          meeting_date: args.meetingDate,
          start_date: args.startDate,
          end_date: args.endDate,
          repport_done: args.reportDone,
        },
      })
    )) as GetMeetingsApiResponse;

    return apiResponse;
  }

  async getBeneficiaryMeetings(args: GetBeneficiaryMeetingsArgs) {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({
        path: `/tele-consultation/beneficiary/${args.beneficiaryId}`,
        searchParams: {
          search: args.search,
          status: args.status,
          meeting_date: args.meetingDate,
          start_date: args.startDate,
          end_date: args.endDate,
          repport_done: args.reportDone,
        },
      })
    )) as Meeting[];

    return apiResponse;
  }

  async create(args: CreateMeetingArgs) {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: "/tele-consultation/create" }),
      {
        beneficiary_id: args.beneficiaryId,
        meeting_date: args.meetingDate,
        start_at: args.startAt,
      }
    );

    return apiResponse;
  }

  async update(args: UpdateMeetingArgs) {
    const apiResponse = await physiomKineApi.patch(
      makeUrl({ path: `/tele-consultation/${args.id}/update/` }),
      {
        beneficiary_id: args.data.beneficiaryId,
        meeting_date: args.data.meetingDate,
        start_at: args.data.startAt,
        status: args.data.status,
        observation_evolution: args.data.observationEvolution,
        observation_exercises: args.data.observationExercises,
        observation_objectifs: args.data.observationObjectifs,
        connectionTrouble: args.data.connectionTrouble,
        is_repport_done: args.data.isReportDone,
      }
    );

    return apiResponse;
  }

  async refreshToken(id: string) {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: `/tele-consultation/${id}/kine-refresh-token/` })
    );

    return apiResponse;
  }

  async getOne(id: string) {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({
        path: `/tele-consultation/${id}/`,
      })
    )) as Meeting;

    return apiResponse;
  }

  async downloadPdf(id: string) {
    const apiResponse = await physiomKineApi.get(
      makeUrl({
        path: `/tele-consultation/${id}/download-report`,
      })
    );

    return apiResponse;
  }
}

export { MeetingService };
export type { GetMeetingsArgs, CreateMeetingArgs, GetBeneficiaryMeetingsArgs };
